import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import amazonScene from 'assets/v3/listing/amazon-scene.webp';
import amazonWhite from 'assets/v3/listing/amazon-white.webp';
import consolePicker from 'assets/v3/listing/console-picker.webp';
import coupangNoText from 'assets/v3/listing/coupang-no-text.webp';
import detailConsole from 'assets/v3/listing/detail-console.webp';
import extensionShot from 'assets/v3/listing/extension-shot.webp';
import heroMainImages from 'assets/v3/listing/hero-main-images.webp';
import marketJp from 'assets/v3/listing/market-jp.webp';
import marketKr from 'assets/v3/listing/market-kr.webp';
import marketNa from 'assets/v3/listing/market-na.webp';
import marketRu from 'assets/v3/listing/market-ru.webp';
import marketSea from 'assets/v3/listing/market-sea.webp';
import ozonHd from 'assets/v3/listing/ozon-hd.webp';
import rakutenVertical from 'assets/v3/listing/rakuten-vertical.webp';

import {JsonLd, buildProductJsonLd, PageCta, PageHero, PageShell, PageTitle, Section} from 'components';
import {ROUTES, URLS} from 'constants/routes';
import {btnPrimaryLg} from 'components/v3/classes';
import useWindowDimensions from 'hooks/useWindowDimensions';

import StepsCarousel from './StepsCarousel';

/**
 * 主图/详情图生成落地页（2026-09，2026-09-16 简化）：原 6 个内容区块砍到 3 个——
 * 合并「平台规范」与「五大市场风格」为一个成品案例展示区，删除「两步生成操作演示」
 * 与「素材输入三种方式」两个区块（详见 docs/superpowers/specs 对应设计文档）。
 *
 * 2026-09-16 二次调整：成品案例改为类瀑布流布局。平台规范图（4:3 为主）与市场风格图
 * （16:10 + 文字说明）交替排布，每张卡片按自身图片的真实宽高比自然撑开高度，不再
 * 强制统一格子尺寸——故意保留这种不规则感。
 *
 * 布局实现刻意不用 CSS `columns`（原生多栏瀑布流）：实测在本项目的 v3-scope 嵌套
 * 结构下，`break-inside-avoid` 卡片会被 Chromium 错误计算为远小于实际列宽的尺寸
 * （已验证是布局引擎问题，不是本组件的类名问题）。改为在 JS 里按“最短列先放”
 * 贪心算法把卡片分配进 N 个独立的纵向 flex 列（用图片宽高比 + 是否带文字说明估算
 * 每张卡片的相对高度），列本身用普通 grid 摆放，彻底避开原生多栏布局的渲染缺陷。
 *
 * 2026-09-17 三次调整：应业务要求把简化时删掉的「两步生成，无门槛使用」与
 * 「三种素材输入方式」两个区块内容融合进同一个区块重新加回——第一步（提供商品素材）
 * 直接把三种素材输入方式作为其展开说明，配 console 操作截图；第二步（AI 自动设计）
 * 保持原样。不再拆成两个独立 Section。
 *
 * 2026-09-17 四次调整：改为三步（新增「高清图生成」步骤，强调按平台规范精准修正
 * 尺寸/比例、直接可上架）；「三种素材输入方式」的两张真实截图（粘贴链接/插件采集，
 * 无上传截图不虚构）与步骤二/三的配图合并进右侧一个轮播（StepsCarousel），
 * 替代原先图文并排的静态布局。
 */

type ShowcaseVariant = 'platform' | 'market';

interface ShowcaseItem {
  image: string;
  key: string;
  variant: ShowcaseVariant;
  /** 图片宽/高，用于贪心分配时估算卡片相对高度 */
  aspectRatio: number;
}

/** 平台图、市场风格图两组交替排布，让每一列从一开始就混合两种比例 */
const showcaseItems: ShowcaseItem[] = [
  {aspectRatio: 4 / 3, image: amazonScene, key: 'amazonScene', variant: 'platform'},
  {aspectRatio: 16 / 10, image: marketNa, key: 'na', variant: 'market'},
  {aspectRatio: 4 / 3, image: amazonWhite, key: 'amazonWhite', variant: 'platform'},
  {aspectRatio: 16 / 10, image: marketJp, key: 'jp', variant: 'market'},
  {aspectRatio: 4 / 3, image: coupangNoText, key: 'coupang', variant: 'platform'},
  {aspectRatio: 16 / 10, image: marketRu, key: 'ru', variant: 'market'},
  {aspectRatio: 4 / 3, image: rakutenVertical, key: 'rakuten', variant: 'platform'},
  {aspectRatio: 16 / 10, image: marketSea, key: 'sea', variant: 'market'},
  {aspectRatio: 900 / 385, image: ozonHd, key: 'ozon', variant: 'platform'},
  {aspectRatio: 16 / 10, image: marketKr, key: 'kr', variant: 'market'},
];

/** market 卡带三行文字说明，估算高度时额外加一段固定权重 */
const estimateRelativeHeight = (item: ShowcaseItem) => 1 / item.aspectRatio + (item.variant === 'market' ? 0.55 : 0.2);

/** 贪心分配：每张卡片放进当前"预估总高度"最小的一列，尽量让各列高度接近 */
const distributeIntoColumns = (items: ShowcaseItem[], columnCount: number): ShowcaseItem[][] => {
  const columns: ShowcaseItem[][] = Array.from({length: columnCount}, () => []);
  const heights = new Array(columnCount).fill(0);

  items.forEach((item) => {
    const shortestIndex = heights.indexOf(Math.min(...heights));
    columns[shortestIndex].push(item);
    heights[shortestIndex] += estimateRelativeHeight(item);
  });

  return columns;
};

const ListingImages: FC = () => {
  const {t} = useTranslation('products');
  const {width} = useWindowDimensions();
  const columnCount = width >= 1024 ? 3 : 2;
  const columns = distributeIntoColumns(showcaseItems, columnCount);

  const renderShowcaseItem = (item: ShowcaseItem) => {
    if (item.variant === 'platform') {
      return (
        <figure className="bg-white rounded-2xl p-3 m-0 shadow-sm" key={item.key}>
          <img
            alt={t(`listing.platform.${item.key}`)}
            className="w-full rounded-xl"
            decoding="async"
            loading="lazy"
            src={item.image}
          />
          <figcaption className="text-sm font-medium text-slate-600 text-center pt-3 pb-1">
            {t(`listing.platform.${item.key}`)}
          </figcaption>
        </figure>
      );
    }

    return (
      <div className="glass-card rounded-2xl p-5" key={item.key}>
        <img
          alt={t(`listing.styles.${item.key}.region`)}
          className="w-full rounded-xl"
          decoding="async"
          loading="lazy"
          src={item.image}
        />
        <div className="px-2 pt-4 pb-1">
          <h3 className="text-base font-semibold text-slate-900 mb-2">{t(`listing.styles.${item.key}.region`)}</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-1.5">{t(`listing.styles.${item.key}.style`)}</p>
          <p className="text-sm text-slate-500 leading-relaxed">{t(`listing.styles.${item.key}.visual`)}</p>
        </div>
      </div>
    );
  };

  return (
    <PageShell>
      <PageTitle
        canonicalUrl="https://www.xiangjifanyi.com/home/listing-images"
        description={t('listing.seo.description')}
        ogDescription={t('listing.seo.ogDescription')}
        title={t('listing.seo.title')}
      />
      <JsonLd
        data={buildProductJsonLd({
          homeLabel: t('common:nav.homeAria'),
          path: '/listing-images',
          productDescription: t('listing.seo.description'),
          productName: t('listing.seo.title'),
        })}
      />
      <PageHero
        actions={
          <a className={btnPrimaryLg} href={URLS.detailPage} rel="noreferrer" target="_blank">
            {t('listing.cta')}
          </a>
        }
        aside={
          <img
            alt={t('listing.title')}
            className="w-full max-w-xl mx-auto rounded-3xl shadow-2xl"
            decoding="async"
            src={heroMainImages}
          />
        }
        description={
          <>
            {t('listing.descLine1')}
            <br />
            {t('listing.descLine2')}
          </>
        }
        title={t('listing.title')}
        variant="light"
      />
      <Section title={t('listing.journeyTitle')}>
        <p className="text-slate-500 lg:text-lg leading-relaxed max-w-3xl mx-auto text-center mb-10">
          {t('listing.journeyDesc')}
        </p>
        <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl p-7">
            <div className="h-1.5 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 mb-5" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2.5">{t('listing.journeyMain.title')}</h3>
            <p className="text-sm lg:text-base text-slate-500 leading-relaxed">{t('listing.journeyMain.desc')}</p>
          </div>
          <div className="glass-card rounded-2xl p-7">
            <div className="h-1.5 w-10 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 mb-5" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2.5">{t('listing.journeyDetail.title')}</h3>
            <p className="text-sm lg:text-base text-slate-500 leading-relaxed">{t('listing.journeyDetail.desc')}</p>
          </div>
        </div>
      </Section>
      <Section bg="muted" title={t('listing.showcaseTitle')} description={t('listing.showcaseDesc')}>
        <div className={`grid gap-5 ${columnCount === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {columns.map((column, columnIndex) => (
            <div className="flex flex-col gap-5" key={`col-${columnIndex}`}>
              {column.map(renderShowcaseItem)}
            </div>
          ))}
        </div>
      </Section>
      <Section bg="muted" title={t('listing.stepsTitle')}>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="space-y-5">
            <div className="bg-white rounded-2xl p-7 shadow-sm">
              <div className="font-display font-bold text-4xl text-indigo-600 mb-4">1</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2.5">{t('listing.stepOne.title')}</h3>
              <p className="text-sm lg:text-base text-slate-500 leading-relaxed mb-4">{t('listing.stepOne.desc')}</p>
              <div className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50/60 p-4">
                <p className="text-xs font-medium text-indigo-600 mb-2.5">{t('listing.sources.hint')}</p>
                <ul className="flex flex-wrap gap-2">
                  <li className="inline-flex items-center rounded-full bg-white px-3.5 py-1.5 text-sm text-slate-700 shadow-sm">
                    {t('listing.sources.upload.title')}
                  </li>
                  <li className="inline-flex items-center rounded-full bg-white px-3.5 py-1.5 text-sm text-slate-700 shadow-sm">
                    {t('listing.sources.link.title')}
                  </li>
                  <li className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-sm text-slate-700 shadow-sm">
                    {t('listing.sources.plugin.title')}
                    <Link className="text-indigo-600 font-medium" to={ROUTES.extension}>
                      {t('listing.sources.plugin.install')}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-7 shadow-sm">
              <div className="font-display font-bold text-4xl text-indigo-600 mb-4">2</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2.5">{t('listing.stepTwo.title')}</h3>
              <p className="text-sm lg:text-base text-slate-500 leading-relaxed">{t('listing.stepTwo.desc')}</p>
            </div>
            <div className="bg-white rounded-2xl p-7 shadow-sm">
              <div className="font-display font-bold text-4xl text-indigo-600 mb-4">3</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2.5">{t('listing.stepThree.title')}</h3>
              <p className="text-sm lg:text-base text-slate-500 leading-relaxed">{t('listing.stepThree.desc')}</p>
            </div>
            <div className="glass-card rounded-2xl p-6 text-sm lg:text-base text-slate-600 leading-relaxed">
              <p className="mb-2.5">{t('listing.kitLine')}</p>
              <p className="text-slate-500">{t('listing.audienceLine')}</p>
            </div>
          </div>
          <StepsCarousel
            slides={[
              {alt: t('listing.sources.link.title'), caption: t('listing.captionDetail'), src: detailConsole},
              {alt: t('listing.sources.plugin.title'), caption: t('listing.captionPlugin'), src: extensionShot},
              {alt: t('listing.stepTwo.title'), caption: t('listing.captionParams'), src: consolePicker},
              {alt: t('listing.stepThree.title'), caption: t('listing.captionOutput'), src: heroMainImages},
            ]}
          />
        </div>
      </Section>
      <PageCta
        actions={
          <a className={btnPrimaryLg} href={URLS.detailPage} rel="noreferrer" target="_blank">
            {t('listing.cta')}
          </a>
        }
        description={t('listing.ctaDesc')}
        title={t('listing.ctaTitle')}
      />
    </PageShell>
  );
};

export default ListingImages;
